import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Promise<any> | undefined} */
  #cruxMlpProductPromise;


  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.sortCthulhuStudOptionValues();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.syncUnavailableOptions();
    this.correctUnavailableSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;
    if (this.#cruxHandleMlpVariantSelection(event.target, selectedOption)) return;
    if (this.#cruxHandleEyeballVariantSelection(event.target, selectedOption)) return;
    if (this.isUnavailableOption(selectedOption)) {
      event.preventDefault();
      this.correctUnavailableSelection();
      return;
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  #cruxIsMlpPicker() {
    return this.dataset.productUrl?.split('?')[0] === '/products/mlp-pinkie-pie-series-ear-accessories-set';
  }

  #cruxMlpOptionGroups() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    if (fieldsets.length) {
      return fieldsets.map((fieldset) => ({
        type: 'fieldset',
        element: fieldset,
        controls: Array.from(fieldset.querySelectorAll('input')),
      }));
    }

    return Array.from(this.querySelectorAll('select')).map((select) => ({
      type: 'select',
      element: select,
      controls: Array.from(select.options),
    }));
  }

  #cruxMlpNormalizeOptionValue(value) {
    return String(value ?? '')
      .replace(/\s+-\s+Unavailable\s*$/i, '')
      .replace(/\s+-\s+Sold out\s*$/i, '')
      .trim();
  }

  #cruxMlpControlValue(control) {
    if (control instanceof HTMLOptionElement) {
      return this.#cruxMlpNormalizeOptionValue(control.textContent.trim() || control.value);
    }
    return this.#cruxMlpNormalizeOptionValue(control.value);
  }

  #cruxMlpSelectedValues() {
    return this.#cruxMlpOptionGroups().map((group) => {
      if (group.type === 'select') {
        const selected = group.element.options[group.element.selectedIndex];
        return selected ? this.#cruxMlpControlValue(selected) : '';
      }

      const checked = group.element.querySelector('input:checked');
      return checked ? checked.value : '';
    });
  }

  #cruxMlpChangedIndex(target) {
    if (target instanceof HTMLSelectElement) {
      return Array.from(this.querySelectorAll('select')).indexOf(target);
    }

    return Number.isNaN(Number(target?.dataset?.fieldsetIndex)) ? -1 : Number(target.dataset.fieldsetIndex);
  }

  #cruxMlpChangedValue(target, selectedOption) {
    if (target instanceof HTMLSelectElement) {
      const selected = target.options[target.selectedIndex];
      return selected ? this.#cruxMlpControlValue(selected) : target.value;
    }

    return selectedOption instanceof HTMLElement ? this.#cruxMlpControlValue(selectedOption) : '';
  }

  #cruxLoadMlpProduct() {
    if (!this.#cruxMlpProductPromise) {
      this.#cruxMlpProductPromise = fetch('/products/mlp-pinkie-pie-series-ear-accessories-set.js')
        .then((response) => response.json())
        .catch(() => null);
    }

    return this.#cruxMlpProductPromise;
  }

  #cruxMlpVariantOptions(variant) {
    return [variant.option1, variant.option2, variant.option3].filter(Boolean);
  }

  #cruxSyncMlpMediaAfterFeaturedMorph(resource, morphElementSelector) {
    if (!this.#cruxIsMlpPicker() || morphElementSelector !== 'featured-product-information') return;

    const selectedMediaId =
      resource?.featured_media?.id?.toString?.() ||
      resource?.featured_image?.id?.toString?.();

    if (!selectedMediaId) return;

    requestAnimationFrame(() => {
      const newPicker = document.querySelector(
        'featured-product-information variant-picker[data-product-url*="mlp-pinkie-pie-series-ear-accessories-set"]'
      );
      const featuredProduct = newPicker?.closest('featured-product-information');
      const gallery = featuredProduct?.querySelector('media-gallery');
      if (!gallery) return;

      const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
      const targetIndex = slides.findIndex((slide) => {
        const media = slide.querySelector('.product-media[data-media-id]');
        return media?.dataset.mediaId === selectedMediaId;
      });

      if (targetIndex < 0) return;
      gallery.slideshow?.select(targetIndex, undefined, { animate: false });
    });
  }

  #cruxChooseMlpCandidate(product, values, lockedIndex, lockedValue) {
    const available = product.variants.filter((variant) => variant.available);
    if (!available.length) return null;

    let candidates = available;
    const lockedProductValue = lockedIndex === 0 ? lockedValue : values[0];
    if (lockedProductValue) {
      const sameProduct = available.filter((variant) => this.#cruxMlpVariantOptions(variant)[0] === lockedProductValue);
      if (sameProduct.length) candidates = sameProduct;
    }

    if (lockedIndex > 0 && lockedValue) {
      const sameLockedValue = candidates.filter(
        (variant) => this.#cruxMlpVariantOptions(variant)[lockedIndex] === lockedValue
      );
      if (sameLockedValue.length) candidates = sameLockedValue;
    }

    return candidates
      .slice()
      .sort((a, b) => {
        const aOptions = this.#cruxMlpVariantOptions(a);
        const bOptions = this.#cruxMlpVariantOptions(b);
        const aScore = aOptions.reduce((score, option, index) => score + (option === values[index] ? 1 : 0), 0);
        const bScore = bOptions.reduce((score, option, index) => score + (option === values[index] ? 1 : 0), 0);
        return bScore - aScore;
      })[0];
  }

  #cruxForceControlAvailable(control) {
    if (control instanceof HTMLOptionElement) {
      control.disabled = false;
      control.dataset.optionAvailable = 'true';
      control.setAttribute('aria-disabled', 'false');
      return;
    }

    if (control instanceof HTMLInputElement) {
      control.disabled = false;
      control.removeAttribute('disabled');
      control.dataset.optionAvailable = 'true';
      control.setAttribute('aria-disabled', 'false');
      control.closest('label')?.removeAttribute('aria-disabled');
    }
  }

  #cruxSetMlpGroupValue(group, value, variantId) {
    if (group.type === 'select') {
      const option = group.controls.find((candidate) => {
        return (
          this.#cruxMlpControlValue(candidate) === value ||
          this.#cruxMlpNormalizeOptionValue(candidate.value) === value
        );
      });
      if (!option) return null;
      this.#cruxForceControlAvailable(option);
      option.dataset.variantId = String(variantId);
      for (const candidate of group.controls) {
        candidate.removeAttribute('selected');
      }
      option.setAttribute('selected', 'selected');
      group.element.value = option.value;
      return group.element;
    }

    const input = group.controls.find((candidate) => candidate.value === value);
    if (!input) return null;
    this.#cruxForceControlAvailable(input);
    input.dataset.variantId = String(variantId);
    input.checked = true;
    input.dataset.currentChecked = 'true';
    return input;
  }

  #cruxApplyMlpCandidate(candidate, preferredTriggerIndex) {
    const groups = this.#cruxMlpOptionGroups();
    const options = this.#cruxMlpVariantOptions(candidate);
    let trigger = null;

    options.forEach((value, index) => {
      const changed = this.#cruxSetMlpGroupValue(groups[index], value, candidate.id);
      if (changed && (trigger === null || index === preferredTriggerIndex || index > preferredTriggerIndex)) {
        trigger = changed;
      }
    });

    return trigger;
  }

  #cruxHandleMlpVariantSelection(target, selectedOption) {
    if (!this.#cruxIsMlpPicker() || this.dataset.cruxMlpNativeRepairing === 'true') return false;
    if (!(target instanceof HTMLElement)) return false;

    const lockedIndex = this.#cruxMlpChangedIndex(target);
    const lockedValue = this.#cruxMlpChangedValue(target, selectedOption);
    if (lockedIndex < 0 || !lockedValue) return false;

    const values = this.#cruxMlpSelectedValues();
    values[lockedIndex] = lockedValue;
    this.dataset.cruxMlpNativeRepairing = 'loading';

    this.#cruxLoadMlpProduct().then((product) => {
      if (!product?.variants?.length || !document.documentElement.contains(this)) return;

      const latestValues = this.#cruxMlpSelectedValues();
      latestValues[lockedIndex] = lockedValue;
      const candidate = this.#cruxChooseMlpCandidate(product, latestValues, lockedIndex, lockedValue);
      if (!candidate) return;

      const trigger = this.#cruxApplyMlpCandidate(candidate, lockedIndex) || target;
      this.dataset.cruxMlpNativeRepairing = 'true';
      requestAnimationFrame(() => {
        trigger.dispatchEvent(new Event('input', { bubbles: true }));
        trigger.dispatchEvent(new Event('change', { bubbles: true }));
        setTimeout(() => {
          delete this.dataset.cruxMlpNativeRepairing;
        }, 800);
      });
    }).catch((error) => {
      console.error(error);
    }).finally(() => {
      if (this.dataset.cruxMlpNativeRepairing === 'loading') {
        delete this.dataset.cruxMlpNativeRepairing;
      }
    });

    return true;
  }

  #cruxEyeballOptionGroups() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    if (fieldsets.length) {
      return fieldsets.map((fieldset) => ({
        type: 'fieldset',
        element: fieldset,
        label: this.#cruxOptionGroupLabel(fieldset),
        controls: Array.from(fieldset.querySelectorAll('input')),
      }));
    }

    return Array.from(this.querySelectorAll('select')).map((select) => ({
      type: 'select',
      element: select,
      label: this.#cruxOptionGroupLabel(select.closest('.variant-option') || select.parentElement || select),
      controls: Array.from(select.options),
    }));
  }

  #cruxOptionGroupLabel(element) {
    const explicit = element?.querySelector?.('legend, label, .variant-option__legend, .form__label');
    const text = explicit?.textContent || element?.textContent || '';
    return this.#cruxNormalizeEyeballValue(text).toLowerCase();
  }

  #cruxIsEyeballThreeOptionPicker() {
    if (this.dataset.cruxMlpNativeRepairing === 'true') return false;
    const productUrl = this.dataset.productUrl || '';
    const groups = this.#cruxEyeballOptionGroups();
    if (groups.length < 3) return false;

    const labels = groups.slice(0, 3).map((group) => group.label);
    const firstLooksLikeEye = /\b(eye|eyeball)\b/i.test(labels[0]) || /cthulhu-eye|eyeball/i.test(productUrl);
    const secondLooksLikeGauge = /gauge|bar style/i.test(labels[1]);
    const thirdLooksLikeLength = /bar length|barbell length|length/i.test(labels[2]);

    return firstLooksLikeEye && secondLooksLikeGauge && thirdLooksLikeLength;
  }

  #cruxNormalizeEyeballValue(value) {
    return String(value ?? '')
      .replace(/\s+-\s+Unavailable\s*$/i, '')
      .replace(/\s+-\s+Sold out\s*$/i, '')
      .trim();
  }

  #cruxEyeballControlValue(control) {
    if (control instanceof HTMLOptionElement) {
      return this.#cruxNormalizeEyeballValue(control.textContent.trim() || control.value);
    }
    return this.#cruxNormalizeEyeballValue(control.value);
  }

  #cruxEyeballChangedIndex(target) {
    if (target instanceof HTMLSelectElement) {
      return Array.from(this.querySelectorAll('select')).indexOf(target);
    }
    return Number.isNaN(Number(target?.dataset?.fieldsetIndex)) ? -1 : Number(target.dataset.fieldsetIndex);
  }

  #cruxEyeballChangedValue(target, selectedOption) {
    if (target instanceof HTMLSelectElement) {
      const selected = target.options[target.selectedIndex];
      return selected ? this.#cruxEyeballControlValue(selected) : target.value;
    }
    return selectedOption instanceof HTMLElement ? this.#cruxEyeballControlValue(selectedOption) : '';
  }

  #cruxLoadExactEyeballVariant(variantId) {
    if (!variantId) return Promise.resolve(null);
    return fetch('/variants/' + encodeURIComponent(variantId) + '.js')
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  #cruxEyeballVariantOptions(variant) {
    if (Array.isArray(variant?.options) && variant.options.length) return variant.options.filter(Boolean);
    return [variant?.option1, variant?.option2, variant?.option3].filter(Boolean);
  }

  #cruxSetEyeballGroupValue(group, value) {
    if (!group) return;

    if (group.type === 'select') {
      const option = group.controls.find((candidate) =>
        this.#cruxEyeballControlValue(candidate) === value ||
        this.#cruxNormalizeEyeballValue(candidate.value) === value
      );
      if (!option) return;

      this.#cruxForceControlAvailable(option);
      for (const candidate of group.controls) {
        candidate.selected = candidate === option;
        if (candidate === option) candidate.setAttribute('selected', 'selected');
        else candidate.removeAttribute('selected');
      }
      group.element.value = option.value;
      return;
    }

    const input = group.controls.find((candidate) => candidate.value === value);
    if (!input) return;
    this.#cruxForceControlAvailable(input);
    for (const candidate of group.controls) {
      const isSelected = candidate === input;
      candidate.checked = isSelected;
      candidate.dataset.currentChecked = isSelected ? 'true' : 'false';
    }
  }

  #cruxApplyExactEyeballVariant(variant) {
    const groups = this.#cruxEyeballOptionGroups();
    this.#cruxEyeballVariantOptions(variant).forEach((value, index) => {
      this.#cruxSetEyeballGroupValue(groups[index], value);
    });
  }

  #cruxSyncEyeballMediaAfterFeaturedMorph(resource, morphElementSelector) {
    if (!this.#cruxIsEyeballThreeOptionPicker() || morphElementSelector !== 'featured-product-information') return;

    const selectedMediaId =
      resource?.featured_media?.id?.toString?.() ||
      resource?.featured_image?.id?.toString?.();
    if (!selectedMediaId) return;

    requestAnimationFrame(() => {
      const featuredProduct = this.closest('featured-product-information') || document.querySelector('featured-product-information');
      const gallery = featuredProduct?.querySelector('media-gallery');
      if (!gallery) return;

      const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
      const targetIndex = slides.findIndex((slide) => {
        const media = slide.querySelector('.product-media[data-media-id]');
        return media?.dataset.mediaId === selectedMediaId;
      });
      if (targetIndex >= 0) gallery.slideshow?.select(targetIndex, undefined, { animate: false });
    });
  }

  #cruxResumeEyeballNativeChange(target) {
    this.dataset.cruxEyeballNativeRepairing = 'true';
    requestAnimationFrame(() => {
      target.dispatchEvent(new Event('input', { bubbles: true }));
      target.dispatchEvent(new Event('change', { bubbles: true }));
      setTimeout(() => {
        delete this.dataset.cruxEyeballNativeRepairing;
      }, 800);
    });
  }

  #cruxHandleEyeballVariantSelection(target, selectedOption) {
    if (!this.#cruxIsEyeballThreeOptionPicker() || this.dataset.cruxEyeballNativeRepairing === 'true') return false;
    if (!(target instanceof HTMLElement)) return false;

    const lockedIndex = this.#cruxEyeballChangedIndex(target);
    const lockedValue = this.#cruxEyeballChangedValue(target, selectedOption);
    const variantId = selectedOption?.dataset?.variantId;
    if (lockedIndex < 0 || !lockedValue || !variantId) return false;

    this.dataset.cruxEyeballNativeRepairing = 'loading';
    this.#cruxLoadExactEyeballVariant(variantId)
      .then((variant) => {
        if (!document.documentElement.contains(this)) return;
        const variantOptions = this.#cruxEyeballVariantOptions(variant);

        // The changed value must remain locked. If Shopify cannot resolve it,
        // hand control back to the theme instead of falling back to Option 1.
        if (!variant || variantOptions[lockedIndex] !== lockedValue) {
          this.#cruxResumeEyeballNativeChange(target);
          return;
        }

        this.#cruxApplyExactEyeballVariant(variant);
        // CRUX: bridge exact eyeball variant to campaign price v1
        // The eye picker repairs the full combination before emitting its native
        // change event. Update the campaign-price component with that exact
        // variant now, rather than leaving a prior bar style's price onscreen.
        const productPrice = this.closest('.shopify-section, dialog')?.querySelector('product-price');
        productPrice?.syncCampaignPrice?.(variant);
        this.#cruxResumeEyeballNativeChange(target);
      })
      .catch(() => this.#cruxResumeEyeballNativeChange(target))
      .finally(() => {
        if (this.dataset.cruxEyeballNativeRepairing === 'loading') {
          delete this.dataset.cruxEyeballNativeRepairing;
        }
      });

    return true;
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
      // CRUX: Cthulhu exact variant id for dynamic price v1
      // The Cthulhu three-option picker repairs all controls to one exact variant.
      // Carry its ID to the section render so product-price does not fall back to
      // a different bar style's price while the picker itself is correct.
      if (this.#cruxIsEyeballThreeOptionPicker() && selectedOption.dataset.variantId) {
        params.push(`variant=${encodeURIComponent(selectedOption.dataset.variantId)}`);
      }
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        const resource = JSON.parse(textContent);
        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
          this.#cruxSyncMlpMediaAfterFeaturedMorph(resource, morphElementSelector);
          this.#cruxSyncEyeballMediaAfterFeaturedMorph(resource, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(resource, this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.sortCthulhuStudOptionValues();
    this.syncUnavailableOptions();
    this.correctUnavailableSelection();
    this.updateVariantPickerCss();

    return newProduct;
  }

  /**
   * Keeps Cthulhu Stud gauge and bar length values in the approved storefront order.
   */
  sortCthulhuStudOptionValues() {
    const sortOrders = {
      'gauge & bar style': [
        '14G | Single Eyeball + Ball End',
        '14G | Single Eyeball + Flat Back',
        '14G | Double Eyeball Barbell',
        '16G | Single Eyeball + Ball End',
        '16G | Single Eyeball + Flat Back',
        '16G | Double Eyeball Barbell',
        '18G | Single Eyeball + Flat Back',
      ],
      'bar length': ['5mm', '6mm', '7mm', '8mm', '9mm', '10mm', '12mm', '14mm', '16mm', '18mm'],
    };

    const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim();
    const orderIndex = (optionName, value, fallback) => {
      const order = sortOrders[optionName];
      if (!order) return fallback;
      const normalizedValue = normalize(value).toLowerCase();
      const index = order.findIndex((item) => normalize(item).toLowerCase() === normalizedValue);
      return index === -1 ? 1000 + fallback : index;
    };
    const optionNameFromFieldset = (fieldset) => {
      const legend = fieldset.querySelector('legend');
      return normalize(legend?.childNodes?.[0]?.textContent || legend?.textContent || '').toLowerCase();
    };
    const sortLabels = (fieldset, optionName) => {
      Array.from(fieldset.querySelectorAll(':scope > label'))
        .map((label, index) => ({ label, index }))
        .sort((a, b) => {
          const aValue = a.label.querySelector('input')?.value || a.label.textContent || '';
          const bValue = b.label.querySelector('input')?.value || b.label.textContent || '';
          return orderIndex(optionName, aValue, a.index) - orderIndex(optionName, bValue, b.index);
        })
        .forEach(({ label }, index) => {
          const input = label.querySelector('input');
          if (input) input.dataset.inputIndex = String(index);
          fieldset.append(label);
        });
    };
    const sortSelect = (select, optionName) => {
      Array.from(select.options)
        .map((option, index) => ({ option, index }))
        .sort((a, b) => orderIndex(optionName, a.option.value, a.index) - orderIndex(optionName, b.option.value, b.index))
        .forEach(({ option }, index) => {
          option.dataset.inputIndex = String(index);
          select.append(option);
        });
    };

    for (const fieldset of this.querySelectorAll('fieldset')) {
      const optionName = optionNameFromFieldset(fieldset);
      if (sortOrders[optionName]) sortLabels(fieldset, optionName);
    }
    for (const select of this.querySelectorAll('select')) {
      const optionName = normalize(select.closest('.variant-option')?.querySelector('label')?.textContent || '').toLowerCase();
      if (sortOrders[optionName]) sortSelect(select, optionName);
    }
  }

  /**
   * Returns whether a picker option cannot currently be purchased.
   * @param {Element} option - Radio input or dropdown option.
   * @returns {boolean}
   */
  isUnavailableOption(option) {
    if (!(option instanceof HTMLElement)) return false;
    return option.dataset.optionAvailable === 'false' || option.getAttribute('aria-disabled') === 'true';
  }

  /**
   * Makes unavailable choices truly non-interactive, while keeping them visible.
   */
  syncUnavailableOptions() {
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('fieldset input[data-option-available]')
    );

    for (const radio of radios) {
      const unavailable = this.isUnavailableOption(radio);
      radio.disabled = unavailable;
      radio.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      radio.closest('label')?.toggleAttribute('aria-disabled', unavailable);
    }

    const options = /** @type {NodeListOf<HTMLOptionElement>} */ (
      this.querySelectorAll('select option[data-option-available]')
    );

    for (const option of options) {
      option.disabled = this.isUnavailableOption(option);
    }
  }

  /**
   * If the current selection lands on an unavailable value after an option refresh,
   * move it to the first available value in that same option group.
   */
  correctUnavailableSelection() {
    const checkedUnavailableRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('fieldset input:checked[data-option-available="false"]')
    );

    if (checkedUnavailableRadio) {
      const replacement = /** @type {HTMLInputElement | null} */ (
        checkedUnavailableRadio
          .closest('fieldset')
          ?.querySelector('input[data-option-available="true"]:not(:disabled)')
      );

      if (replacement && replacement !== checkedUnavailableRadio) {
        requestAnimationFrame(() => replacement.click());
        return;
      }
    }

    const selects = /** @type {NodeListOf<HTMLSelectElement>} */ (this.querySelectorAll('select'));
    for (const select of selects) {
      const selected = select.options[select.selectedIndex];
      if (!selected || !this.isUnavailableOption(selected)) continue;

      const replacement = Array.from(select.options).find((option) => !this.isUnavailableOption(option));
      if (replacement) {
        select.value = replacement.value;
        requestAnimationFrame(() => select.dispatchEvent(new Event('change', { bubbles: true })));
        return;
      }
    }
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
